import { createColumnHelper } from "@tanstack/react-table";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { dateFormat } from "@/core/helpers/dateFormat";
import { getDiffTimeDDHHmm } from "@/core/helpers/getDiffTimeDDHHmm";
import { Phone2Icon } from "@/shared/icons";
import Corner from "@/shared/widgets/Corner";
import { getRowGradient } from "@/domains/faction/helpers/getRowGradient";

export default function useHistoryColumns() {
  const { t } = useTranslation("faction");

  // Участники ===========================================================================
  const getParticipantsColumns = useCallback(() => {
    // TODO добавить тип, когда будут данные с бэкенда |||||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const columnHelper = createColumnHelper<any>();

    return [
      columnHelper.accessor(
        (row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""} ${row?.id ?? ""}`.trim(),
        {
          id: "who",
          header: t("faction.tableTitles.who"),
          cell: (info) => (
            <div>
              <Corner
                className="top-auto right-auto left-0 bottom-2.5 -rotate-180"
                classNameSvg="text-dark-900"
              />
              {info.getValue() ?? "-"}
            </div>
          ),
          meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
        },
      ),
      columnHelper.accessor(
        (row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""} ${row?.id ?? ""}`.trim(),
        {
          id: "whom",
          header: t("faction.tableTitles.whom"),
          cell: (info) => info.getValue() ?? "-",
          meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
        },
      ),
      columnHelper.accessor("action", {
        header: t("faction.tableTitles.action"),
        cell: (info) => <div className="text-[#81C39A]">{info.getValue() ?? "-"}</div>,
        meta: { isSort: true },
      }),
      columnHelper.accessor("reason", {
        header: t("faction.tableTitles.reason"),
        cell: (info) => info.getValue() || "-",
        meta: { isSort: true },
      }),
      columnHelper.accessor("createdAt", {
        header: t("faction.tableTitles.dateOfSubmission"),
        cell: (info) => {
          const value = info.getValue();
          const dateAndTime = value ? dateFormat(value, true).split(" ") : "";
          return (
            <div>
              {value ? (
                <div>
                  {dateAndTime[0]} <span className="text-white/70">{dateAndTime[1]}</span>
                </div>
              ) : (
                "-"
              )}
              <div
                className="absolute top-0 right-0 w-25 h-[calc(100%-0.625rem)] rounded-[0.4375rem]"
                style={{ background: getRowGradient(value ? "#405C4A" : "#6D6D6D") }}
              />
              <Corner className="top-auto bottom-2.5 rotate-90" classNameSvg="text-dark-900" />
            </div>
          );
        },
        meta: { isSort: true },
      }),
    ];
  }, [t]);

  // ФИНАНСЫ =============================================================================
  const getFinanceColumns = useCallback(() => {
    // TODO добавить тип, когда будут данные с бэкенда |||||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const columnHelper = createColumnHelper<any>();

    return [
      columnHelper.accessor(
        (row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""} ${row?.id ?? ""}`.trim(),
        {
          id: "who",
          header: t("faction.tableTitles.who"),
          cell: (info) => (
            <div>
              <Corner
                className="top-auto right-auto left-0 bottom-2.5 -rotate-180"
                classNameSvg="text-dark-900"
              />
              {info.getValue() ?? "-"}
            </div>
          ),
          meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
        },
      ),
      columnHelper.accessor(
        (row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""} ${row?.id ?? ""}`.trim(),
        {
          id: "whom",
          header: t("faction.tableTitles.whom"),
          cell: (info) => info.getValue() ?? "-",
          meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
        },
      ),
      columnHelper.accessor("action", {
        header: t("faction.tableTitles.action"),
        cell: (info) => info.getValue() || "-",
        meta: { isSort: true },
      }),
      columnHelper.accessor("reason", {
        header: t("faction.tableTitles.reason"),
        cell: (info) => info.getValue() || "-",
        meta: { isSort: true },
      }),
      columnHelper.accessor("createdAt", {
        header: t("faction.tableTitles.dateOfSubmission"),
        cell: (info) => {
          const value = info.getValue();
          const dateAndTime = value ? dateFormat(value, true).split(" ") : "";
          return (
            <div>
              {value ? (
                <div>
                  {dateAndTime[0]} <span className="text-white/70">{dateAndTime[1]}</span>
                </div>
              ) : (
                "-"
              )}
              <div
                className="absolute top-0 right-0 w-25 h-[calc(100%-0.625rem)] rounded-[0.4375rem]"
                style={{ background: getRowGradient(value ? "#405C4A" : "#6D6D6D") }}
              />
              <Corner className="top-auto bottom-2.5 rotate-90" classNameSvg="text-dark-900" />
            </div>
          );
        },
        meta: { isSort: true },
      }),
    ];
  }, [t]);

  // Склад ===============================================================================
  const getWarehouseColumns = useCallback(() => {
    // TODO добавить тип, когда будут данные с бэкенда |||||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const columnHelper = createColumnHelper<any>();

    return [
      columnHelper.accessor(
        (row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""} ${row?.id ?? ""}`.trim(),
        {
          id: "who",
          header: t("faction.tableTitles.who"),
          cell: (info) => (
            <div>
              <Corner
                className="top-auto right-auto left-0 bottom-2.5 -rotate-180"
                classNameSvg="text-dark-900"
              />
              {info.getValue() ?? "-"}
            </div>
          ),
          meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
        },
      ),
      columnHelper.accessor(
        (row) => `${row?.firstName ?? ""} ${row?.lastName ?? ""} ${row?.id ?? ""}`.trim(),
        {
          id: "whom",
          header: t("faction.tableTitles.whom"),
          cell: (info) => info.getValue() ?? "-",
          meta: { isSort: true, style: { minWidth: "12rem", maxWidth: "12rem" } },
        },
      ),
      columnHelper.accessor("action", {
        header: t("faction.tableTitles.action"),
        cell: (info) => info.getValue() || "-",
        meta: { isSort: true },
      }),
      columnHelper.accessor("reason", {
        header: t("faction.tableTitles.reason"),
        cell: (info) => info.getValue() || "-",
        meta: { isSort: true },
      }),
      columnHelper.accessor("createdAt", {
        header: t("faction.tableTitles.dateOfSubmission"),
        cell: (info) => {
          const value = info.getValue();
          const dateAndTime = value ? dateFormat(value, true).split(" ") : "";
          return (
            <div>
              {value ? (
                <div>
                  {dateAndTime[0]} <span className="text-white/70">{dateAndTime[1]}</span>
                </div>
              ) : (
                "-"
              )}
              <div
                className="absolute top-0 right-0 w-25 h-[calc(100%-0.625rem)] rounded-[0.4375rem]"
                style={{ background: getRowGradient(value ? "#405C4A" : "#6D6D6D") }}
              />
              <Corner className="top-auto bottom-2.5 rotate-90" classNameSvg="text-dark-900" />
            </div>
          );
        },
        meta: { isSort: true },
      }),
    ];
  }, [t]);

  return {
    getParticipantsColumns,
    getFinanceColumns,
    getWarehouseColumns,
  };
}
